import { logEvent, setUserId, setUserProperties as setFirebaseUserProperties } from 'firebase/analytics';
import { analytics } from '../config/firebaseConfig';
import posthog from 'posthog-js';

/**
 * Comprehensive Analytics Manager for dual platform tracking
 * Handles Firebase Analytics and PostHog integration
 */
class AnalyticsManager {
  constructor() {
    this.isInitialized = false;
    this.firebaseInitialized = false;
    this.posthogInitialized = false;
    this.userConsent = this.loadUserConsent();
    this.eventQueue = [];
    // Only enable analytics in production (www.44agents.com)
    this.isProduction = window.location.hostname === 'www.44agents.com';
    this.isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    this.debugMode = !this.isProduction || localStorage.getItem('analytics_debug') === 'true';
  }

  /**
   * Initialize both analytics platforms
   */
  async initializeAnalytics() {
    // Only initialize analytics in production
    if (!this.isProduction) {
      return;
    }

    try {
      await Promise.all([
        this.initializeFirebase(),
        this.initializePostHOG()
      ]);
      
      this.isInitialized = true;
      this.processQueuedEvents();
      
    } catch (error) {
      this.handleAnalyticsError(error, 'initialization');
    }
  }

  /**
   * Initialize Firebase Analytics
   */
  async initializeFirebase() {
    try {
      // Only initialize Firebase Analytics in production
      if (analytics && this.userConsent && this.isProduction) {
        this.firebaseInitialized = true;
      } else {
        this.firebaseInitialized = false;
      }
    } catch (error) {
      this.handleAnalyticsError(error, 'firebase_initialization');
    }
  }

  /**
   * Initialize PostHog
   */
  async initializePostHOG() {
    try {
      // Only initialize PostHog in production
      if (this.isProduction && this.userConsent) {
        const posthogKey = 'phc_7noTJjnjHoS99Qy4Ku0KcyVVUyTYja1JuOH4oQpg6VZ';
        const posthogHost = 'https://us.posthog.com';
        
        posthog.init(posthogKey, {
          api_host: posthogHost,
          capture_pageview: false,
          capture_pageleave: true,
          persistence: 'localStorage+cookie',
          session_recording: {
            maskAllInputs: false,
            maskInputOptions: { 
              password: true,
              email: false
            }
          },
          person_profiles: 'identified_only',
          opt_in_site_apps: true,
          respect_dnt: true,
          autocapture: true,
          capture_performance: true,
          secure_cookie: true,
          cross_subdomain_cookie: false
        });
        
        this.posthogInitialized = true;
        
      }
    } catch (error) {
      this.handleAnalyticsError(error, 'posthog_initialization');
    }
  }

  /**
   * Track event on both platforms
   * @param {string} eventName - Event name (snake_case)
   * @param {object} properties - Event properties
   */
  trackEvent(eventName, properties = {}) {
    // Only track events in production
    if (!this.isProduction) {
      return;
    }

    // Validate and sanitize
    if (!this.validateEventData(eventName, properties)) {
      return;
    }

    const sanitizedProperties = this.sanitizeProperties(properties);
    const enhancedProperties = this.enhanceProperties(sanitizedProperties);

    // Queue events if not initialized
    if (!this.isInitialized) {
      this.eventQueue.push({ eventName, properties: enhancedProperties });
      return;
    }

    // Track on both platforms
    this.trackFirebaseEvent(eventName, enhancedProperties);
    this.trackPostHogEvent(eventName, enhancedProperties);

  }

  /**
   * Track Firebase event
   */
  trackFirebaseEvent(eventName, properties) {
    if (!this.firebaseInitialized || !this.userConsent) return;

    try {
      // Firebase has a 40 character limit for event names
      const firebaseEventName = this.formatFirebaseEventName(eventName);
      logEvent(analytics, firebaseEventName, properties);
    } catch (error) {
      this.handleAnalyticsError(error, 'firebase_event');
    }
  }

  /**
   * Track PostHog event
   */
  trackPostHogEvent(eventName, properties) {
    if (!this.posthogInitialized || !this.userConsent) return;

    try {
      posthog.capture(eventName, properties);
    } catch (error) {
      this.handleAnalyticsError(error, 'posthog_event');
    }
  }

  /**
   * Identify user across platforms
   */
  identifyUser(userId, userProperties = {}) {
    if (!userId) return;

    const sanitizedProperties = this.sanitizeProperties(userProperties);

    // Firebase
    if (this.firebaseInitialized) {
      try {
        setUserId(analytics, userId);
        setFirebaseUserProperties(analytics, sanitizedProperties);
      } catch (error) {
        this.handleAnalyticsError(error, 'firebase_identify');
      }
    }

    // PostHog
    if (this.posthogInitialized) {
      try {
        posthog.identify(userId, sanitizedProperties);
      } catch (error) {
        this.handleAnalyticsError(error, 'posthog_identify');
      }
    }
  }

  /**
   * Set user properties
   */
  setUserProperties(properties) {
    // In development, log user properties to console for debugging
    if (this.isDevelopment) {
      console.log(`[Analytics] Set User Properties:`, properties);
      return;
    }

    const sanitizedProperties = this.sanitizeProperties(properties);

    if (this.firebaseInitialized) {
      try {
        setFirebaseUserProperties(analytics, sanitizedProperties);
      } catch (error) {
        this.handleAnalyticsError(error, 'firebase_properties');
      }
    }

    if (this.posthogInitialized) {
      try {
        posthog.people.set(sanitizedProperties);
      } catch (error) {
        this.handleAnalyticsError(error, 'posthog_properties');
      }
    }
  }

  /**
   * Create user group for B2B analytics (PostHog only)
   */
  createUserGroup(groupType, groupId, groupProperties = {}) {
    if (!this.posthogInitialized) return;

    try {
      posthog.group(groupType, groupId, groupProperties);
    } catch (error) {
      this.handleAnalyticsError(error, 'posthog_group');
    }
  }

  /**
   * Track page view
   */
  trackPageView(pageName, properties = {}) {
    const pageProperties = {
      page_name: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...properties
    };

    this.trackEvent('page_view', pageProperties);
  }

  /**
   * Track timing events (performance)
   */
  trackTiming(category, variable, value, label = '') {
    this.trackEvent('timing_complete', {
      timing_category: category,
      timing_variable: variable,
      timing_value: value,
      timing_label: label
    });
  }

  /**
   * Validate event data
   */
  validateEventData(eventName, properties) {
    // Check event name
    if (!eventName || typeof eventName !== 'string') {
      return false;
    }

    // Check for reserved event names
    const reservedNames = ['session_start', 'session_end'];
    if (reservedNames.includes(eventName)) {
    }

    // Validate properties
    if (properties && typeof properties !== 'object') {
      return false;
    }

    return true;
  }

  /**
   * Sanitize properties to remove PII
   */
  sanitizeProperties(properties) {
    if (!properties || typeof properties !== 'object') {
      return {};
    }

    const sanitized = { ...properties };
    const piiFields = ['password', 'ssn', 'credit_card', 'cvv'];
    
    // Remove PII fields
    piiFields.forEach(field => {
      delete sanitized[field];
    });

    // Mask email if needed
    if (sanitized.email && !this.shouldTrackEmail()) {
      sanitized.email = this.maskEmail(sanitized.email);
    }

    // Ensure property values are valid types
    Object.keys(sanitized).forEach(key => {
      const value = sanitized[key];
      if (value === undefined || value === null) {
        delete sanitized[key];
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        sanitized[key] = JSON.stringify(value);
      }
    });

    return sanitized;
  }

  /**
   * Enhance properties with additional context
   */
  enhanceProperties(properties) {
    return {
      ...properties,
      timestamp: new Date().toISOString(),
      session_id: this.getSessionId(),
      platform: 'web',
      environment: this.isProduction ? 'production' : 'development'
    };
  }

  /**
   * Format event name for Firebase (40 char limit)
   */
  formatFirebaseEventName(eventName) {
    // Replace spaces and special characters
    let formatted = eventName.toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .substring(0, 40);
    
    // Ensure it starts with a letter
    if (!/^[a-z]/.test(formatted)) {
      formatted = 'e_' + formatted.substring(0, 38);
    }
    
    return formatted;
  }

  /**
   * Process queued events
   */
  processQueuedEvents() {
    if (this.eventQueue.length === 0) return;

    const queue = [...this.eventQueue];
    this.eventQueue = [];

    queue.forEach(({ eventName, properties }) => {
      this.trackEvent(eventName, properties);
    });
  }

  /**
   * Handle analytics errors
   */
  handleAnalyticsError(error, context) {
    // Could send to error reporting service in the future
    // For now, just ignore the error silently
  }

  /**
   * User consent management
   */
  setUserConsent(hasConsent) {
    this.userConsent = hasConsent;
    localStorage.setItem('analytics_consent', hasConsent ? 'true' : 'false');
    
    if (hasConsent && !this.isInitialized) {
      this.initializeAnalytics();
    } else if (!hasConsent) {
      this.optOut();
    }
  }

  /**
   * Load user consent from storage
   */
  loadUserConsent() {
    const consent = localStorage.getItem('analytics_consent');
    return consent !== 'false'; // Default to true if not set
  }

  /**
   * Check if email tracking is allowed
   */
  shouldTrackEmail() {
    return localStorage.getItem('track_email') !== 'false';
  }

  /**
   * Mask email for privacy
   */
  maskEmail(email) {
    const [local, domain] = email.split('@');
    const maskedLocal = local.substring(0, 2) + '***';
    return `${maskedLocal}@${domain}`;
  }

  /**
   * Get or create session ID
   */
  getSessionId() {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Opt out of analytics
   */
  optOut() {
    if (this.posthogInitialized) {
      posthog.opt_out_capturing();
    }
    
    // Clear any stored data
    this.eventQueue = [];
  }

  /**
   * Reset analytics (useful for logout)
   */
  reset() {
    if (this.posthogInitialized) {
      posthog.reset();
    }
    
    sessionStorage.removeItem('analytics_session_id');
    this.eventQueue = [];
  }
}

// Create singleton instance
const analyticsManager = new AnalyticsManager();

// Export instance and key methods
export default analyticsManager;

export const {
  initializeAnalytics,
  trackEvent,
  identifyUser,
  setUserProperties,
  createUserGroup,
  trackPageView,
  trackTiming,
  setUserConsent,
  reset
} = {
  initializeAnalytics: () => analyticsManager.initializeAnalytics(),
  trackEvent: (eventName, properties) => analyticsManager.trackEvent(eventName, properties),
  identifyUser: (userId, properties) => analyticsManager.identifyUser(userId, properties),
  setUserProperties: (properties) => analyticsManager.setUserProperties(properties),
  createUserGroup: (type, id, properties) => analyticsManager.createUserGroup(type, id, properties),
  trackPageView: (pageName, properties) => analyticsManager.trackPageView(pageName, properties),
  trackTiming: (category, variable, value, label) => analyticsManager.trackTiming(category, variable, value, label),
  setUserConsent: (consent) => analyticsManager.setUserConsent(consent),
  reset: () => analyticsManager.reset()
};